import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Save, RotateCcw, FileDown, MonitorPlay, Plus, Trash2, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Bar, Legend } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import PresentationMode from "./PresentationMode";
import AcompanhamentoInventario from "@/components/AcompanhamentoInventario";

const MESES = ["jan/26", "fev/26", "mar/26", "abr/26", "mai/26", "jun/26", "jul/26", "ago/26", "set/26", "out/26", "nov/26", "dez/26"];
const LOJAS = ["BSC", "GV", "ASSAI", "RADIAL B", "DD", "OP", "GUA"];
const LOJA_TODAS = "__todas__";
const LISTAS = [
  { tipo: "evolucoes", label: "Evoluções" },
  { tipo: "pontos", label: "Pontos de Atenção" },
  { tipo: "fontes", label: "Fontes de Divergência" },
  { tipo: "acoes", label: "Ações Corretivas" },
  { tipo: "oportunidades", label: "Oportunidades 2026" },
];

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function Inventario() {
  const [data, setData] = useState([]);
  const [listas, setListas] = useState([]);
  const [meta, setMeta] = useState(95);
  const [mes, setMes] = useState("jun/26");
  const [loja, setLoja] = useState("BSC");
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presenting, setPresenting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/inventario/report");
      setData(res.data.itens);
      setListas(res.data.listas);
      setMeta(res.data.meta_acuracidade);
      const m = {};
      res.data.itens.forEach((d) => {
        if (!m[d.mes]) m[d.mes] = {};
        m[d.mes][d.loja] = { acuracidade: d.acuracidade ?? "", custo: d.custo ?? "" };
      });
      setMatrix(m);
    } catch {
      toast.error("Erro ao carregar inventário");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sel = useMemo(() => {
    if (loja === LOJA_TODAS) {
      const rows = data.filter((d) => d.mes === mes);
      const acuracidades = rows.map((r) => r.acuracidade).filter((v) => v != null);
      const custos = rows.map((r) => r.custo).filter((v) => v != null);
      return {
        acuracidade: acuracidades.length ? acuracidades.reduce((a, b) => a + b, 0) / acuracidades.length : null,
        custo: custos.length ? custos.reduce((a, b) => a + b, 0) : null,
        _count: rows.length,
        _lojasComAcur: acuracidades.length,
      };
    }
    return data.find((d) => d.mes === mes && d.loja === loja) || {};
  }, [data, mes, loja]);
  const custoTotalAno = useMemo(() => data.reduce((a, d) => a + (d.custo || 0), 0), [data]);

  // Custo/média por loja para o card dourado quando uma loja específica está selecionada
  const custoLojaAno = useMemo(() => {
    if (loja === LOJA_TODAS) return custoTotalAno;
    return data.filter((d) => d.loja === loja).reduce((a, d) => a + (d.custo || 0), 0);
  }, [data, loja, custoTotalAno]);

  const mediaAcurLoja = useMemo(() => {
    if (loja === LOJA_TODAS) return null;
    const vals = data.filter((d) => d.loja === loja && d.acuracidade != null).map((d) => d.acuracidade);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [data, loja]);

  const mediaCustoLoja = useMemo(() => {
    if (loja === LOJA_TODAS) return null;
    const vals = data.filter((d) => d.loja === loja && d.custo != null).map((d) => d.custo);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [data, loja]);

  const evolucao = useMemo(() => {
    if (loja === LOJA_TODAS) {
      return MESES.map((m) => {
        const rows = data.filter((x) => x.mes === m);
        const acuracidades = rows.map((r) => r.acuracidade).filter((v) => v != null);
        return { mes: m, acuracidade: acuracidades.length ? Number((acuracidades.reduce((a, b) => a + b, 0) / acuracidades.length).toFixed(2)) : null };
      }).filter((e) => e.acuracidade !== null);
    }
    return MESES.map((m) => {
      const d = data.find((x) => x.mes === m && x.loja === loja);
      return { mes: m, acuracidade: d?.acuracidade ?? null };
    }).filter((e) => e.acuracidade !== null);
  }, [data, loja]);

  const evolucaoPP = useMemo(() => {
    if (evolucao.length < 2) return 0;
    const last = evolucao[evolucao.length - 1].acuracidade;
    const prev = evolucao[evolucao.length - 2].acuracidade;
    return (last - prev).toFixed(2);
  }, [evolucao]);

  const financeiro = useMemo(() =>
    MESES.map((m) => {
      const rows = data.filter((d) => d.mes === m);
      const custo = rows.reduce((a, d) => a + (d.custo || 0), 0);
      const media = rows.length ? rows.reduce((a, d) => a + (d.acuracidade || 0), 0) / rows.length : null;
      return { mes: m, custo: Math.round(custo * 100) / 100, media: media ? Math.round(media * 100) / 100 : null };
    }),
  [data]);

  const mesesAbaixoMeta = useMemo(() => {
    if (loja === LOJA_TODAS) {
      return MESES.filter((m) => {
        const rows = data.filter((x) => x.mes === m);
        const acs = rows.map((r) => r.acuracidade).filter((v) => v != null);
        if (!acs.length) return false;
        return acs.reduce((a, b) => a + b, 0) / acs.length < meta;
      });
    }
    return MESES.filter((m) => {
      const d = data.find((x) => x.mes === m && x.loja === loja);
      return d && (d.acuracidade ?? 100) < meta;
    });
  }, [data, loja, meta]);

  const setCell = (m, l, field, value) => {
    setMatrix((prev) => ({
      ...prev,
      [m]: { ...(prev[m] || {}), [l]: { ...(prev[m]?.[l] || {}), [field]: value } },
    }));
  };

  const saveBulk = async () => {
    setSaving(true);
    try {
      const itens = [];
      MESES.forEach((m) => LOJAS.forEach((l) => {
        const c = matrix[m]?.[l];
        if (!c) return;
        itens.push({
          mes: m, loja: l,
          acuracidade: c.acuracidade === "" || c.acuracidade == null ? null : Number(c.acuracidade),
          custo: c.custo === "" || c.custo == null ? null : Number(c.custo),
        });
      }));
      await api.post("/inventario/bulk-save", { itens });
      await api.put(`/inventario/meta?meta=${Number(meta)}`);
      toast.success("Matrizes salvas (snapshot mensal atualizado)");
      fetchAll();
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async (incluirAcomp = false) => {
    const params = {};
    if (loja !== LOJA_TODAS) params.loja = loja;
    if (mes) params.mes = mes;
    if (incluirAcomp) params.incluir_acompanhamento = true;
    const res = await api.get("/inventario/export/pdf", { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    const suffix = incluirAcomp ? `_${mes}_${loja}_acompanhamento` : "";
    a.href = url; a.download = `inventario${suffix}.pdf`.replace(/[/ ]/g, "-"); a.click();
    URL.revokeObjectURL(url);
  };

  const addListaItem = async (tipo) => {
    const res = await api.post("/inventario/lista", { tipo, conteudo: "", ordem: listas.filter((l) => l.tipo === tipo).length });
    setListas((prev) => [...prev, res.data]);
  };

  const updateListaItem = async (item, patch) => {
    setListas((prev) => prev.map((l) => (l.id === item.id ? { ...l, ...patch } : l)));
    await api.patch(`/inventario/lista/${item.id}`, { ...item, ...patch });
  };

  const deleteListaItem = async (id) => {
    await api.delete(`/inventario/lista/${id}`);
    setListas((prev) => prev.filter((l) => l.id !== id));
  };

  const autoGrow = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const acur = sel.acuracidade ?? null;
  const dentroMeta = acur !== null && acur >= meta;

  if (loading) return <div className="py-20 text-center text-[#5A6B62]" data-testid="inventario-loading">Carregando inventários...</div>;

  return (
    <div className="space-y-8" data-testid="inventario-page">
      {/* Header — tema safira/dourado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F6B3A]">Inventários & Auditoria</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Acuracidade de estoque · Governança de inventário</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="inventario-salvar-btn" onClick={saveBulk} disabled={saving}
            className="bg-[#0F6B3A] hover:bg-[#0B5530] disabled:opacity-60 text-white font-semibold px-4 py-2.5 rounded-lg shadow-sm flex items-center gap-2 transition-colors">
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar meses"}
          </button>
          <button data-testid="inventario-restaurar-btn" onClick={fetchAll}
            className="bg-white hover:bg-[#F4F8F5] text-[#0F6B3A] border border-[#D9E3DC] font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
            <RotateCcw className="w-4 h-4" /> Restaurar
          </button>
          <button data-testid="inventario-pdf-btn" onClick={() => exportPdf(false)}
            className="bg-white hover:bg-[#EAF7EF] text-[#22A85B] border border-[#B7E4C7] font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
            <FileDown className="w-4 h-4" /> Exportar PDF
          </button>
          <button data-testid="inventario-pdf-acomp-btn" onClick={() => exportPdf(true)}
            disabled={loja === LOJA_TODAS}
            title={loja === LOJA_TODAS ? "Selecione uma loja específica para incluir o Acompanhamento" : `Exportar ${mes} · ${loja} com Acompanhamento`}
            className="bg-[#EAF7EF] hover:bg-[#D3F2DF] text-[#0F6B3A] border border-[#0F6B3A] font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <FileDown className="w-4 h-4" /> PDF + Acompanhamento
          </button>
          <button data-testid="inventario-apresentacao-btn" onClick={() => setPresenting(true)}
            className="bg-gradient-to-r from-[#0F6B3A] to-[#0B5530] hover:from-[#0B5530] hover:to-[#0B1F17] text-white border border-[#2DC26B] font-semibold px-5 py-2.5 rounded-lg shadow-md flex items-center gap-2.5 transition-all">
            <MonitorPlay className="w-4 h-4" /> Modo Apresentação
          </button>
        </div>
      </div>

      {/* Seção 1 */}
      <section className="bg-white border border-[#E3ECE6] rounded-xl shadow-sm p-5">
        <h2 className="text-lg font-semibold text-[#0F6B3A]" data-testid="inventario-secao-1">Inventários realizados</h2>
        <p className="text-sm text-[#5A6B62] mt-1">{data.length} registros de auditoria · {LOJAS.length} lojas · {MESES.length} competências (jan–dez/26)</p>
      </section>

      {/* Seção 2 */}
      <section className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <div>
            <label className="text-xs font-semibold text-[#0F6B3A] uppercase tracking-wide">Mês</label>
            <select data-testid="inventario-mes-select" value={mes} onChange={(e) => setMes(e.target.value)}
              className="mt-1 bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F6B3A] font-medium focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]">
              {MESES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#0F6B3A] uppercase tracking-wide">Loja</label>
            <select data-testid="inventario-loja-select" value={loja} onChange={(e) => setLoja(e.target.value)}
              className="mt-1 bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F6B3A] font-medium focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]">
              <option value={LOJA_TODAS}>★ Média — Todas as lojas</option>
              {LOJAS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Card dourado — muda entre agregado total vs. por loja selecionada */}
          <div data-testid="inventario-gold-cost-card"
            className="rounded-xl shadow-md p-6 border-2 border-[#B7E4C7]"
            style={{ background: "linear-gradient(135deg, #EAF7EF 0%, #D3F2DF 100%)" }}>
            {loja === LOJA_TODAS ? (
              <>
                <p className="text-xs font-bold text-[#22A85B] uppercase tracking-widest">Custo total acumulado no ano</p>
                <p className="text-4xl font-extrabold text-[#2DC26B] mt-3">{fmtBRL(custoTotalAno)}</p>
                <p className="text-sm text-[#22A85B] mt-2">Soma das divergências de todos os meses e lojas</p>
              </>
            ) : (
              <>
                <p className="text-xs font-bold text-[#22A85B] uppercase tracking-widest">Loja {loja} — Consolidado do ano</p>
                <p className="text-4xl font-extrabold text-[#2DC26B] mt-3" data-testid="loja-custo-total">{fmtBRL(custoLojaAno)}</p>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-[#B7E4C7]/60">
                  <div>
                    <p className="text-[10px] font-bold text-[#22A85B] uppercase tracking-wide">Média Custo/mês</p>
                    <p className="text-lg font-extrabold text-[#0F6B3A]" data-testid="loja-media-custo">{mediaCustoLoja != null ? fmtBRL(mediaCustoLoja) : "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#22A85B] uppercase tracking-wide">Média Acuracidade</p>
                    <p className="text-lg font-extrabold text-[#0F6B3A]" data-testid="loja-media-acur">{mediaAcurLoja != null ? `${mediaAcurLoja.toFixed(2)}%` : "—"}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 5 KPIs */}
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-[#D9E3DC] rounded-xl p-4" data-testid="inv-kpi-acuracidade">
              <p className="text-[11px] font-semibold text-[#5A6B62] uppercase tracking-wide">
                {loja === LOJA_TODAS ? `Média Acuracidade (${mes} · Todas as lojas)` : `Acuracidade (${mes} · ${loja})`}
              </p>
              <p className="text-2xl font-extrabold text-[#0F6B3A] mt-1">{acur !== null ? `${acur.toFixed(2)}%` : "—"}</p>
              {loja === LOJA_TODAS && sel._lojasComAcur != null && (
                <p className="text-[10px] text-[#8A9A90] mt-1">{sel._lojasComAcur} de {LOJAS.length} lojas com dado</p>
              )}
            </div>
            <div className="bg-white border border-[#D9E3DC] rounded-xl p-4" data-testid="inv-kpi-meta">
              <p className="text-[11px] font-semibold text-[#5A6B62] uppercase tracking-wide">Meta %</p>
              <input data-testid="inventario-meta-input" type="number" step="0.1" value={meta} onChange={(e) => setMeta(Number(e.target.value))}
                className="mt-1 w-full text-xl font-extrabold text-[#2DC26B] bg-transparent border-b border-[#B7E4C7] focus:outline-none focus:border-[#2DC26B]" />
            </div>
            <div className="bg-white border border-[#D9E3DC] rounded-xl p-4" data-testid="inv-kpi-evolucao">
              <p className="text-[11px] font-semibold text-[#5A6B62] uppercase tracking-wide">Evolução p.p.</p>
              <p className={`text-2xl font-extrabold mt-1 ${Number(evolucaoPP) >= 0 ? "text-[#047857]" : "text-[#B91C1C]"}`}>
                {Number(evolucaoPP) >= 0 ? "+" : ""}{evolucaoPP}
              </p>
            </div>
            <div className="bg-white border border-[#D9E3DC] rounded-xl p-4" data-testid="inv-kpi-custo">
              <p className="text-[11px] font-semibold text-[#5A6B62] uppercase tracking-wide">
                {loja === LOJA_TODAS ? "Total Divergência R$" : "Divergência R$ Custo"}
              </p>
              <p className="text-2xl font-extrabold text-[#2DC26B] mt-1">{sel.custo != null ? fmtBRL(sel.custo) : "—"}</p>
            </div>
            <div className={`rounded-xl p-4 border ${dentroMeta ? "bg-[#ECFDF5] border-[#A7F3D0]" : "bg-[#FEF2F2] border-[#FECACA]"}`} data-testid="inv-kpi-status">
              <p className="text-[11px] font-semibold text-[#5A6B62] uppercase tracking-wide">Status</p>
              <p className={`text-lg font-extrabold mt-1 ${dentroMeta ? "text-[#047857]" : "text-[#B91C1C]"}`}>
                {acur === null ? "Sem dados" : dentroMeta ? "Dentro da meta" : "Abaixo da meta"}
              </p>
            </div>
          </div>
        </div>

        {/* Banner alerta */}
        {mesesAbaixoMeta.length > 0 && (
          <div data-testid="inventario-alerta-meta" className="flex items-center gap-3 bg-[#EAF7EF] border border-[#B7E4C7] rounded-lg px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-[#2DC26B] shrink-0" />
            <p className="text-sm text-[#22A85B]">
              <strong>Alerta de meta:</strong>{" "}
              {loja === LOJA_TODAS
                ? <>a <strong>média das lojas</strong> ficou abaixo da meta de {meta}% em:{" "}<strong>{mesesAbaixoMeta.join(", ")}</strong></>
                : <>a loja <strong>{loja}</strong> ficou abaixo da meta de {meta}% em:{" "}<strong>{mesesAbaixoMeta.join(", ")}</strong></>}
            </p>
          </div>
        )}

        {/* Evolução */}
        <div className="bg-white border border-[#E3ECE6] rounded-xl shadow-sm p-5">
          <h3 className="text-base font-semibold text-[#0F6B3A] mb-4">
            {loja === LOJA_TODAS ? "Evolução — média de todas as lojas — jan a dez/26" : `Evolução da loja ${loja} — jan a dez/26`}
          </h3>
          <div className="h-64" data-testid="inventario-chart-evolucao">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evolucao} margin={{ top: 5, right: 10, left: -14, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#8A9A90" }} />
                <YAxis domain={["auto", 100]} tick={{ fontSize: 10, fill: "#8A9A90" }} unit="%" />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D9E3DC", borderRadius: 8, fontSize: 12 }} />
                <ReferenceLine y={meta} stroke="#2DC26B" strokeDasharray="6 4" strokeWidth={2}
                  label={{ value: `Meta ${meta}%`, position: "insideTopRight", fill: "#2DC26B", fontSize: 11, fontWeight: 700 }} />
                <Line type="monotone" dataKey="acuracidade" name="Acuracidade %" stroke="#0F6B3A" strokeWidth={2.5} dot={{ r: 4, fill: "#0F6B3A" }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Seção 2b — Matrizes */}
      <section className="space-y-4" data-testid="inventario-matrizes">
        <h2 className="text-lg font-semibold text-[#0F6B3A]">Matrizes mensais editáveis</h2>
        {[{ field: "acuracidade", label: "Acuracidade %", testid: "matriz-acuracidade" },
          { field: "custo", label: "Custo R$ (divergência)", testid: "matriz-custo" }].map(({ field, label, testid }) => (
          <div key={field} className="bg-white border border-[#E3ECE6] rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E3ECE6] bg-[#F4F8F5]">
              <h3 className="text-sm font-bold text-[#0F6B3A]">{label}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid={testid}>
                <thead>
                  <tr className="border-b border-[#E3ECE6] text-[#0F6B3A] font-bold uppercase tracking-wide">
                    <th className="text-left py-2.5 px-3 sticky left-0 bg-white">Mês</th>
                    {LOJAS.map((l) => <th key={l} className="py-2.5 px-3">{l}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {MESES.map((m) => (
                    <tr key={m} className="border-b border-[#F4F8F5] hover:bg-[#F4F8F5]/60">
                      <td className="py-2 px-3 font-bold text-[#0F6B3A] sticky left-0 bg-white">{m}</td>
                      {LOJAS.map((l) => (
                        <td key={l} className="py-1.5 px-2">
                          <input
                            data-testid={`matriz-${field}-${m}-${l}`}
                            type="number" step={field === "acuracidade" ? "0.01" : "1"}
                            value={matrix[m]?.[l]?.[field] ?? ""}
                            onChange={(e) => setCell(m, l, field, e.target.value)}
                            className="w-20 text-center bg-white border border-[#D9E3DC] rounded px-1.5 py-1 text-xs text-[#0F2A1F] focus:outline-none focus:ring-1 focus:ring-[#0F6B3A]"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Impacto financeiro + média lojas */}
        <div className="bg-white border border-[#E3ECE6] rounded-xl shadow-sm p-5">
          <h3 className="text-base font-semibold text-[#0F6B3A] mb-4">Impacto financeiro por mês & histórico (média das lojas)</h3>
          <div className="h-72" data-testid="inventario-chart-financeiro">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={financeiro} margin={{ top: 5, right: 10, left: -4, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#8A9A90" }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#8A9A90" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <YAxis yAxisId="right" orientation="right" domain={[80, 100]} tick={{ fontSize: 10, fill: "#8A9A90" }} unit="%" />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D9E3DC", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, name) => (name === "Custo R$" ? fmtBRL(v) : `${v}%`)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="custo" name="Custo R$" fill="#2DC26B" radius={[4, 4, 0, 0]} maxBarSize={30} />
                <Line yAxisId="right" type="monotone" dataKey="media" name="Média lojas %" stroke="#0F6B3A" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Nova seção — Acompanhamento do Inventário (accordion, não altera nada acima) */}
      {loja !== LOJA_TODAS && (
        <AcompanhamentoInventario
          mes={mes}
          loja={loja}
          snapshot={data.find((d) => d.mes === mes && d.loja === loja)}
          meta={meta}
          canEdit={true}
          lojasDisponiveis={LOJAS}
        />
      )}

      {/* Seção 3 — Listas editáveis */}
      <section className="space-y-4" data-testid="inventario-secao-3">
        <h2 className="text-lg font-semibold text-[#0F6B3A]">Análises & Planos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {LISTAS.map(({ tipo, label }) => {
            const itens = listas.filter((l) => l.tipo === tipo);
            return (
              <div key={tipo} className="bg-white border border-[#E3ECE6] rounded-xl shadow-sm p-4" data-testid={`lista-${tipo}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#0F6B3A]">{label}</h3>
                  <button data-testid={`lista-${tipo}-add`} onClick={() => addListaItem(tipo)}
                    className="p-1.5 rounded-md text-[#0F6B3A] hover:bg-[#EAF7EF] transition-colors" title="Adicionar item">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  {itens.length === 0 && <p className="text-xs text-[#8A9A90]">Nenhum item. Clique em + para adicionar.</p>}
                  {itens.map((item) => (
                    <div key={item.id} className="border border-[#E3ECE6] rounded-lg p-3 space-y-2 bg-[#FDFDFE]">
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`lista-item-titulo-${item.id}`}
                          value={item.titulo || ""}
                          onChange={(e) => updateListaItem(item, { titulo: e.target.value })}
                          placeholder="Título (editável)"
                          className="flex-1 text-xs font-semibold text-[#0F6B3A] bg-transparent border-b border-transparent hover:border-[#D9E3DC] focus:border-[#0F6B3A] focus:outline-none px-1 py-0.5"
                        />
                        <button data-testid={`lista-item-del-${item.id}`} onClick={() => deleteListaItem(item.id)}
                          className="p-1 rounded text-[#B91C1C] hover:bg-[#FEF2F2]" title="Remover">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <textarea
                        data-testid={`lista-item-conteudo-${item.id}`}
                        value={item.conteudo}
                        onChange={(e) => updateListaItem(item, { conteudo: e.target.value })}
                        onInput={autoGrow}
                        rows={2}
                        placeholder="Descreva..."
                        className="w-full text-sm text-[#5A6B62] bg-white border border-[#E3ECE6] rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#0F6B3A] resize-none overflow-hidden"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {presenting && (
        <PresentationMode
          data={data} meta={meta} mes={mes} loja={loja} meses={MESES} lojas={LOJAS}
          evolucao={evolucao} financeiro={financeiro} listas={listas}
          onClose={() => setPresenting(false)}
        />
      )}
    </div>
  );
}
